/**
 * Video Popup Player Web Component
 * Creates a modal popup with video player when triggered
 */
if (!customElements.get('video-popup-player')) {
class VideoPopupPlayer extends HTMLElement {
  constructor() {
    super();
    this.modal = null;
    this.videoPlayer = null;
    this.isOpen = false;
  }

  connectedCallback() {
    // Use setTimeout to ensure child elements are fully parsed
    setTimeout(() => {
      this.setupTrigger();
    }, 0);
  }

  setupTrigger() {
    const trigger = this.querySelector('.video-popup-trigger');
    if (trigger) {
      // Make entire trigger clickable
      trigger.style.cursor = 'pointer';
      
      // Add click handler to trigger
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openPopup();
      });
      
      // Also add click handler to thumbnail as backup
      const thumbnail = trigger.querySelector('.video-preview-thumbnail');
      if (thumbnail) {
        thumbnail.style.cursor = 'pointer';
        thumbnail.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openPopup();
        });
      }
      
      // Add click handler to play button
      const playButton = trigger.querySelector('.video-play-button');
      if (playButton) {
        playButton.style.cursor = 'pointer';
        playButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.openPopup();
        });
      }
    } else {
      console.warn('Video popup trigger not found for:', this);
    }
  }

  openPopup() {
    if (this.isOpen) return;

    let videoSrc = this.getAttribute('src') || '';
    const videoPoster = this.getAttribute('poster') || '';
    const hasControls = this.hasAttribute('controls');
    const hasAutoplay = this.hasAttribute('autoplay');

    // Handle Shopify video object format
    if (!videoSrc || videoSrc === 'undefined' || videoSrc === 'null' || videoSrc.trim() === '') {
      console.error('Video source is required. Current src:', videoSrc);
      return;
    }

    // Ensure videoSrc is a string URL
    if (typeof videoSrc === 'object') {
      if (videoSrc.sources && videoSrc.sources.length > 0) {
        videoSrc = videoSrc.sources[0].url;
      } else {
        console.error('Invalid video source format:', videoSrc);
        return;
      }
    }

    // Ensure videoSrc is a valid URL string
    if (typeof videoSrc !== 'string' || videoSrc.trim() === '') {
      console.error('Video source must be a non-empty string. Got:', typeof videoSrc, videoSrc);
      return;
    }
    
    // Normalize protocol-relative URLs (//example.com -> https://example.com)
    if (videoSrc.startsWith('//')) {
      videoSrc = window.location.protocol + videoSrc;
    }

    this.isOpen = true;
    this.createModal(videoSrc, videoPoster, hasControls, hasAutoplay);
    document.body.appendChild(this.modal);
    document.body.style.overflow = 'hidden';

    // Focus trap and close on escape
    this.modal.focus();
    this.modal.addEventListener('keydown', this.handleKeyDown.bind(this));

    // Animate in
    requestAnimationFrame(() => {
      this.modal.classList.add('is-open');
    });
  }

  createModal(videoSrc, videoPoster, hasControls, hasAutoplay) {
    this.modal = document.createElement('div');
    this.modal.className = 'video-popup-modal';
    this.modal.setAttribute('role', 'dialog');
    this.modal.setAttribute('aria-modal', 'true');
    this.modal.setAttribute('aria-label', 'Video player');
    this.modal.tabIndex = -1;

    const overlay = document.createElement('div');
    overlay.className = 'video-popup-overlay';
    overlay.addEventListener('click', () => this.closePopup());

    const container = document.createElement('div');
    container.className = 'video-popup-container';

    const closeButton = document.createElement('button');
    closeButton.className = 'video-popup-close';
    closeButton.setAttribute('aria-label', 'Close video');
    closeButton.innerHTML = `
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    closeButton.addEventListener('click', (e) => {
      e.stopPropagation();
      this.closePopup();
    });

    // Wait for media-video-player to be defined
    if (customElements.get('media-video-player')) {
      this.videoPlayer = document.createElement('media-video-player');
      // Ensure the player has proper styling
      this.videoPlayer.style.display = 'block';
      this.videoPlayer.style.width = '100%';
      this.videoPlayer.style.position = 'relative';
      
      this.videoPlayer.setAttribute('src', videoSrc);
      if (videoPoster) {
        this.videoPlayer.setAttribute('poster', videoPoster);
      }
      if (hasControls) {
        this.videoPlayer.setAttribute('controls', '');
      }
      if (hasAutoplay) {
        this.videoPlayer.setAttribute('autoplay', '');
      }
    } else {
      // Fallback: create a regular video element
      console.warn('media-video-player not found, using fallback video element');
      this.videoPlayer = document.createElement('video');
      this.videoPlayer.src = videoSrc;
      this.videoPlayer.controls = hasControls;
      this.videoPlayer.autoplay = hasAutoplay;
      if (videoPoster) {
        this.videoPlayer.poster = videoPoster;
      }
      this.videoPlayer.style.width = '100%';
      this.videoPlayer.style.height = '100%';
      this.videoPlayer.style.display = 'block';
    }

    container.appendChild(closeButton);
    container.appendChild(this.videoPlayer);
    this.modal.appendChild(overlay);
    this.modal.appendChild(container);
    
    // Ensure autoplay works by programmatically playing after a short delay
    if (hasAutoplay) {
      setTimeout(() => {
        if (this.videoPlayer) {
          const videoElement = this.videoPlayer.querySelector('video') || (this.videoPlayer.tagName === 'VIDEO' ? this.videoPlayer : null);
          if (videoElement) {
            videoElement.play().catch(err => {
              console.warn('Autoplay failed:', err);
            });
          } else if (this.videoPlayer.play) {
            // If media-video-player has a play method
            this.videoPlayer.play().catch(err => {
              console.warn('Autoplay failed:', err);
            });
          }
        }
      }, 100);
    }
  }

  closePopup() {
    if (!this.isOpen) return;

    this.isOpen = false;
    document.body.style.overflow = '';

    if (this.videoPlayer) {
      // Pause video before closing
      if (typeof this.videoPlayer.pause === 'function') {
        this.videoPlayer.pause();
      } else if (this.videoPlayer.video && typeof this.videoPlayer.video.pause === 'function') {
        this.videoPlayer.video.pause();
      }
    }

    this.modal.classList.remove('is-open');

    setTimeout(() => {
      if (this.modal && this.modal.parentNode) {
        this.modal.parentNode.removeChild(this.modal);
      }
      this.modal = null;
      this.videoPlayer = null;
    }, 300);
  }

  handleKeyDown(e) {
    if (e.key === 'Escape') {
      this.closePopup();
    }
  }
}

// Register the custom element
customElements.define('video-popup-player', VideoPopupPlayer);
}
